// Dismissing a paid-but-cancelled item as test data (0317). Superadmin only —
// the function refuses anyone else. It never refunds or edits the payment; the
// item just leaves the card, so the note that says why is required and audited.

import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { type PaidWithoutBookingRow, useDismissPaidWithoutBooking } from "./support.hooks";

const NOTE_MAX = 500;

interface DismissPaidItemDialogProps {
  item: PaidWithoutBookingRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DismissPaidItemDialog({ item, open, onOpenChange }: DismissPaidItemDialogProps) {
  const dismiss = useDismissPaidWithoutBooking();
  const [note, setNote] = useState("");
  const trimmed = note.trim();
  const noteId = `dismiss-paid-note-${item.payment_id}`;
  const refs = item.booking_refs?.length ? item.booking_refs.join(", ") : "this payment";

  function change(next: boolean) {
    if (!next) setNote("");
    onOpenChange(next);
  }

  async function submit() {
    if (!trimmed) return;
    try {
      await dismiss.mutateAsync({ paymentId: item.payment_id, note: trimmed });
      change(false);
    } catch (error) {
      toast.error("Couldn't dismiss the item", {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  return (
    <Dialog onOpenChange={change} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Dismiss <span className="font-mono">{refs}</span> as test?
          </DialogTitle>
          <DialogDescription>
            It leaves this list for good. Nothing is refunded or changed — only use this for our own
            test bookings.
          </DialogDescription>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor={noteId}>Why is this test data?</FieldLabel>
          <Textarea
            id={noteId}
            maxLength={NOTE_MAX}
            onChange={(event) => setNote(event.target.value)}
            placeholder="e.g. August test bookings"
            required
            rows={3}
            value={note}
          />
          <FieldDescription>Required. Kept with your name in the audit log.</FieldDescription>
        </Field>

        <DialogFooter>
          <Button disabled={dismiss.isPending} onClick={() => change(false)} variant="outline">
            Cancel
          </Button>
          <Button disabled={!trimmed} loading={dismiss.isPending} onClick={() => void submit()}>
            Dismiss as test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
